"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Paintbrush, Code, Lightbulb, Palette, Terminal, Braces, FileCode, Layers } from "lucide-react"

export function AudienceSplitHero() {
  const [activeSection, setActiveSection] = useState<"creative" | "developer">("creative")

  const creativeIcons = [Paintbrush, Palette, Lightbulb, Layers]
  const developerIcons = [Code, Terminal, Braces, FileCode]

  return (
    <section className="py-24 px-4 relative overflow-hidden backdrop-blur-sm shadow-card" id="who-its-for">
      <div className="absolute inset-0 z-0"></div>
      <div className="container mx-auto relative z-10 ">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Who It's For</h2>
          <p className="text-xl text-card-foreground max-w-3xl mx-auto">
            One platform, endless possibilities for every type of creator
          </p>
        </div>

        {/* <div className="flex flex-col lg:flex-row gap-4 h-[600px]"> */}
        <div className="flex flex-col lg:flex-row gap-4">
          <motion.div
            className={`relative flex-1 rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ease-out ${activeSection === "creative" ? "lg:flex-[2]" : "lg:flex-1"}`}
            onClick={() => setActiveSection("creative")}
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-pink-600/20 to-purple-900/40 z-0"></div>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm z-10"></div>

            <div className="relative h-full z-20 p-8 flex flex-col">
              <div className="flex items-center mb-6">
                <div className="p-2 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500 mr-3">
                  <Paintbrush className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-2xl font-bold">For Creatives & Marketers</h3>
              </div>

              <div
                className={`transition-all duration-500 ${activeSection === "creative" ? "opacity-100" : "opacity-0 lg:opacity-100"}`}
              >
                <p className="text-card-foreground/30 mb-8">
                  No coding required. Create stunning websites with drag-and-drop simplicity while maintaining complete
                  creative control.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <div className="p-1 rounded-full bg-pink-500/20 mr-3 mt-1">
                      <div className="h-2 w-2 rounded-full bg-pink-500"></div>
                    </div>
                    <p className="text-card-foreground/30">Visual editing with real-time preview</p>
                  </div>
                  <div className="flex items-start">
                    <div className="p-1 rounded-full bg-pink-500/20 mr-3 mt-1">
                      <div className="h-2 w-2 rounded-full bg-pink-500"></div>
                    </div>
                    <p className="text-card-foreground/30">AI-powered design suggestions</p>
                  </div>
                  <div className="flex items-start">
                    <div className="p-1 rounded-full bg-pink-500/20 mr-3 mt-1">
                      <div className="h-2 w-2 rounded-full bg-pink-500"></div>
                    </div>
                    <p className="text-card-foreground/30">Pre-built templates and components</p>
                  </div>
                </div>
              </div>

              <div
                className={`mt-auto mb-12 grid grid-cols-4 gap-4 transition-all duration-500 ${activeSection === "creative" ? "opacity-100" : "opacity-50"}`}
              >
                {creativeIcons.map((Icon, index) => (
                  <motion.div
                    key={index}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                    className="flex items-center justify-center h-12 w-12 rounded-lg bg-gradient-to-br from-pink-500/20 to-purple-500/20 border border-pink-500/30"
                  >
                    <Icon className="h-6 w-6 text-pink-300" />
                  </motion.div>
                ))}
              </div>

              <motion.div
                className={`absolute bottom-8 m right-8 px-4 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500 font-medium transition-all duration-300 ${activeSection === "creative" ? "opacity-100" : "opacity-0 lg:opacity-100"}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Explore No-Code
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            className={`relative flex-1 rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ease-out ${activeSection === "developer" ? "lg:flex-[2]" : "lg:flex-1"}`}
            onClick={() => setActiveSection("developer")}
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/20 to-blue-900/40 z-0"></div>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm z-10"></div>

            <div className="relative h-full z-20 p-8 flex flex-col">
              <div className="flex items-center mb-6">
                <div className="p-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 mr-3">
                  <Code className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-2xl font-bold">For Developers & Engineers</h3>
              </div>

              <div
                className={`transition-all duration-500 ${activeSection === "developer" ? "opacity-100" : "opacity-0 lg:opacity-100"}`}
              >
                <p className="text-card-foreground/30 mb-8">
                  Full code control with advanced features. Extend functionality with your own code while leveraging AI
                  to accelerate development.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <div className="p-1 rounded-full bg-cyan-500/20 mr-3 mt-1">
                      <div className="h-2 w-2 rounded-full bg-cyan-500"></div>
                    </div>
                    <p className="text-card-foreground/30">Direct code editing with syntax highlighting</p>
                  </div>
                  <div className="flex items-start">
                    <div className="p-1 rounded-full bg-cyan-500/20 mr-3 mt-1">
                      <div className="h-2 w-2 rounded-full bg-cyan-500"></div>
                    </div>
                    <p className="text-card-foreground/30">Custom component creation and reuse</p>
                  </div>
                  <div className="flex items-start">
                    <div className="p-1 rounded-full bg-cyan-500/20 mr-3 mt-1">
                      <div className="h-2 w-2 rounded-full bg-cyan-500"></div>
                    </div>
                    <p className="text-card-foreground/30">API integration and advanced logic</p>
                  </div>
                </div>
              </div>

              <div
                className={`mt-auto grid grid-cols-4 mb-12 gap-4 transition-all duration-500 ${activeSection === "developer" ? "opacity-100" : "opacity-50"}`}
              >
                {developerIcons.map((Icon, index) => (
                  <motion.div
                    key={index}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                    className="flex items-center justify-center h-12 w-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30"
                  >
                    <Icon className="h-6 w-6 text-cyan-300" />
                  </motion.div>
                ))}
              </div>

              <motion.div
                className={`absolute bottom-8 right-8 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 font-medium transition-all duration-300 ${activeSection === "developer" ? "opacity-100" : "opacity-0 lg:opacity-100"}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Explore Code Mode
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
